import React, { Key, useState, useEffect } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Tag } from 'antd';
import { ArrowClockwise, Plus } from '@phosphor-icons/react';
import { ColumnSelector, TableCommon, SkeletonTable, TopHeader, ButtonCommon, SelectCommon } from 'components/common';
import { Columns, QueryParams } from 'types';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from 'utils';
import { buildFullParams, parseSimpleFilter } from 'utils/filterBuilder';
import { setTableParams, TableCacheItem } from 'stores/slices/tableCacheSlice';
import { EInvoice, InvoiceRecord } from 'models/EInvoice';
import {
  canAdjustInvoice,
  canEditDraftInvoice,
  canReplaceInvoice,
  getInvoiceTagColor,
  getInvoiceStatusTextClass,
} from 'constant/invoiceStatus';
import { format } from 'date-fns';
import { useEInvoiceOperation } from 'hooks/useEInvoiceOperation';
import FilterEInvoice from 'components/common/Filter/FilterEInvoice';
import { useGetAllTemplateEInvoicesQuery } from 'services';
import { EInvoiceFilter } from 'types/invoice.type';
import useAuthContext from 'stores/auth-context';
import { hasPermission } from 'utils/permission';
import { PERMISSIONS } from 'constant/permission';
import envService, { HiddenFeatures } from 'services/env.service';

const InvoiceListPage: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const isPreviewMode = params.get('preview') === 'true';
  const [eInvoice, setEInvoice] = useState<InvoiceRecord[]>([]);
  const [isLoadingDownload, setIsLoadingDownload] = useState<boolean>(false);
  const [idFileDownload, setIdFileDownload] = useState<string>();
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<string>();
  const [isResettingList, setIsResettingList] = useState<boolean>(false);
  const [refreshSpinKey, setRefreshSpinKey] = useState<number>(0);

  const { data: templateEInvoiceData, isLoading: isLoadingTemplate } = useGetAllTemplateEInvoicesQuery({
    query: { year: new Date().getFullYear() },
  });

  const {
    handleDownload,
    eInvoiceData,
    isLoading,
    isFetching,
    refetch,
    filteredQuery,
    setFilteredQuery,
    dispatch,
    pageKey,
    tableState,
    handleApplyFilter,
    handleClearFilter,
    listMode,
  } = useEInvoiceOperation();

  const navigate = useNavigate();
  const { profile } = useAuthContext();
  const permissionKeys = profile?.data?.permissionKeys || [];
  const canCreateInvoice = hasPermission(PERMISSIONS.E_INVOICE_CREATE, permissionKeys);
  const bypassInvoiceEligibilityValidation = envService.isFeatureHidden(HiddenFeatures.ValidateInvoice);
  const invoiceCounterLabel =
    listMode === 'adjustments' ? 'hóa đơn điều chỉnh' : listMode === 'replacements' ? 'hóa đơn thay thế' : 'hóa đơn';

  const templateOptions = templateEInvoiceData?.data?.templates
    ?.filter((p) => p?.khmshDon === '2')
    ?.map((p, index) => ({
      label: (
        <div className='flex items-center justify-between gap-2'>
          <span className='font-medium truncate'>{[p?.thDon, p?.khhDon]?.filter(Boolean)?.join(' - ')}</span>
          <span className='text-[12px] text-gray-500 flex-shrink-0'>
            {p?.cLai}/{p?.sLuong}
          </span>
        </div>
      ),
      value: p?.khhDon,
      key: index,
    }));

  useEffect(() => {
    if (eInvoiceData?.data?.rows) {
      setEInvoice(eInvoiceData.data.rows);
      dispatch(
        setTableParams({
          pageKey,
          params: {
            lastFetched: Date.now(),
            search: filteredQuery.searchValue ?? '',
            page: filteredQuery.page ?? 1,
            pageSize: filteredQuery.size ?? 10,
            filter: filteredQuery.filter ?? '',
          } as unknown as Partial<TableCacheItem<unknown>>,
        }),
      );
    }
  }, [eInvoiceData]);

  useEffect(() => {
    if (!templateEInvoiceData?.data?.templates?.length) return;
    const templates = templateEInvoiceData.data.templates.filter((p) => p?.khmshDon === '2');
    if (!templates.length) return;
    // Chỉ set mặc định khi CHƯA có KHHDon
    if (!selectedInvoiceType) {
      const first = templates[0];
      if (first.khhDon) {
        setSelectedInvoiceType(first.khhDon);
        setFilteredQuery((prev) => ({
          ...prev,
          ...({ KHHDon: first.khhDon } as unknown as QueryParams),
        }));
      }
    } else {
      setSelectedInvoiceType(selectedInvoiceType);
    }
  }, [templateEInvoiceData]);

  const handleReplace = (record: InvoiceRecord): void => {
    try {
      navigate('/invoices/electronic-invoices/create-invoice', { state: { replaceInvoice: record } });
    } catch (error) {
      console.log(error);
    }
  };

  const handleAdjust = (record: InvoiceRecord): void => {
    try {
      navigate('/invoices/electronic-invoices/create-invoice', { state: { adjustInvoice: record } });
    } catch (error) {
      console.log(error);
    }
  };

  const eInvoiceColumns: ColumnsType<InvoiceRecord> = [
    {
      title: 'SỐ HOÁ ĐƠN',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 140,
      fixed: 'left',
      ellipsis: true,
      render: (value) => <span className='font-mono text-[13px] font-semibold text-gray-900'>{value || '---'}</span>,
    },
    {
      title: 'TRẠNG THÁI',
      dataIndex: 'invoiceTypeText',
      key: 'invoiceTypeText',
      width: 160,
      sorter: (a, b) => (a?.invoiceTypeText || '')?.localeCompare((b?.invoiceTypeText || '') as string),
      filterSearch: false,
      render: (text, record) => {
        const status = record.status;
        const invoiceType = record.invoiceType;
        const color = getInvoiceTagColor(status, invoiceType);
        const statusText = record.statusText;
        return (
          <div className='flex flex-col gap-1'>
            <Tag
              color={color}
              className='inline-flex max-w-fit items-center rounded-full border-0 px-3 py-1 text-[11px] font-semibold tracking-[0.04em] shadow-sm'
              style={{ margin: 0 }}
            >
              {text ? text.toUpperCase() : '---'}
            </Tag>
            {statusText && (
              <div className={`text-[11px] font-medium ${getInvoiceStatusTextClass(status)}`}>{statusText}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'NGÀY LẬP',
      dataIndex: 'signedAt',
      key: 'signedAt',
      width: 140,
      render: (date) => {
        if (!date) return <div>---</div>;

        return <div className='text-sm text-gray-700'>{format(new Date(date), 'HH:mm, dd/MM/yyyy')}</div>;
      },
    },
    {
      title: 'MÃ THAM CHIẾU',
      dataIndex: 'referenceCode',
      key: 'referenceCode',
      width: 150,
      ellipsis: true,
      render: (value) => <span className='text-sm text-gray-700'>{value || '---'}</span>,
    },
    {
      title: 'TÊN KHÁCH HÀNG',
      dataIndex: 'buyerName',
      key: 'buyerName',
      width: 300,
      ellipsis: true,
      render: (value) => <span className='font-medium text-gray-900'>{value || 'Khách lẻ'}</span>,
    },
    { title: 'MÃ SỐ THUẾ', dataIndex: 'buyerTaxCode', key: 'buyerTaxCode', width: 120 },
    {
      title: 'GIÁ TRỊ',
      dataIndex: 'totalAfterTax',
      key: 'totalAfterTax',
      width: 160,
      align: 'right',
      fixed: 'right',
      render: (text) => <span className='text-sm font-semibold text-gray-900'>{formatPrice(text)} VNĐ</span>,
    },
  ];

  const eInvoiceColumnsCheck: Columns[] = eInvoiceColumns.map((column) => ({
    key: column.key as string,
    title: column.title as string,
  }));

  const [checkedList, setCheckedList] = useState<(Key | undefined)[]>(eInvoiceColumns.map((column) => column.key));

  const getFilteredColumns = () => {
    return eInvoiceColumns.map((item) => ({
      ...item,
      hidden: !checkedList.includes(item.key),
    }));
  };

  const handlePagination = (query: QueryParams) => {
    const columnArray = ['status.code'];
    const filterQuery = buildFullParams(query, undefined, columnArray);
    setFilteredQuery((prev) => ({ ...prev, ...filterQuery }));
  };

  function handleView(record: InvoiceRecord): void {
    if (canEditDraftInvoice(record)) {
      navigate('/invoices/electronic-invoices/create-invoice', { state: { draftInvoice: record } });
    } else {
      const invoiceDetailUrl = `/invoices/electronic-invoices/view/${record.id}`;
      window.open(invoiceDetailUrl, '_blank', 'noopener,noreferrer');
    }
  }

  async function handleDownloadFile(record: EInvoice): Promise<void> {
    try {
      if (isLoadingDownload || idFileDownload) return;
      setIsLoadingDownload(true);
      setIdFileDownload(record.id);
      await handleDownload(record.id);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingDownload(false);
      setIdFileDownload('');
    }
  }

  const handleCreateInvoice = (): void => {
    navigate('/invoices/electronic-invoices/create-invoice');
  };

  const handleResetInvoiceList = async (): Promise<void> => {
    setRefreshSpinKey((prev) => prev + 1);
    setIsResettingList(true);
    handleClearFilter(selectedInvoiceType);
    try {
      await refetch();
    } finally {
      window.setTimeout(() => {
        setIsResettingList(false);
      }, 300);
    }
  };

  // --- RENDERING ---
  return (
    <div className='flex h-[calc(94vh-56px)] min-h-0 flex-col overflow-hidden bg-[var(--bgGreyColor)]'>
      {!isPreviewMode && (
        <TopHeader
          className='mb-0 rounded-none border-b'
          title='DANH SÁCH HOÁ ĐƠN ĐIỆN TỬ'
          subTitle='Tất cả các hoá đơn điện tử được hiển thị tại đây'
        />
      )}

      <div className='flex-1 min-h-0 px-4 py-4 md:px-3 md:py-3'>
        <div className='flex h-full min-h-0 items-stretch gap-3'>
          <div className='w-[320px] flex-shrink-0'>
            <div className='flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
              <div className='border-b border-gray-100 px-5 py-4'>
                <div className='text-lg font-semibold text-gray-900'>Bộ lọc</div>
                <div className='text-sm text-gray-500'>Tinh chỉnh danh sách hoá đơn theo điều kiện tra cứu.</div>
              </div>

              <div className='flex-1 min-h-0'>
                <FilterEInvoice
                  initialFilter={{ listMode }}
                  onApplyFilter={handleApplyFilter}
                  onClearFilter={() => handleClearFilter(selectedInvoiceType)}
                />
              </div>
            </div>
          </div>

          <div className='min-w-0 flex-1'>
            <div className='flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
              <div className='shrink-0 border-b border-gray-100 px-5 py-4'>
                <div className='flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between'>
                  <div className='flex flex-col gap-4'>
                    {/* <div className='flex items-start gap-3'>
                      <div>
                        <div className='text-lg font-semibold text-gray-900'>Danh sách hoá đơn</div>
                        <div className='text-sm text-gray-500'>
                          Theo dõi trạng thái phát hành, tra cứu và thao tác nhanh trên từng hoá đơn.
                        </div>
                      </div>
                    </div> */}

                    <div className='flex flex-col gap-3 lg:flex-row lg:items-center'>
                      <div className='w-full lg:w-[320px]'>
                        <SelectCommon
                          loading={isLoadingTemplate}
                          value={selectedInvoiceType}
                          placeholder='Loại hoá đơn'
                          options={templateOptions}
                          onChange={(value) => {
                            setSelectedInvoiceType(value);
                            setFilteredQuery((prev) => ({
                              ...prev,
                              ...({ KHHDon: value } as unknown as QueryParams),
                            }));
                            handleApplyFilter({ KHHDon: String(value) } as unknown as EInvoiceFilter);
                          }}
                        />
                      </div>

                      <div className='inline-flex w-fit items-center rounded-full bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700'>
                        {eInvoiceData?.data?.total || 0} {invoiceCounterLabel}
                      </div>
                    </div>
                  </div>

                  <div className='flex flex-wrap items-center gap-2 xl:justify-end'>
                    <ButtonCommon
                      color='outline'
                      onClick={handleResetInvoiceList}
                      size='xs'
                      radius='rounded'
                      className='w-[40px] p-0'
                      title='Reset danh sách hoá đơn điện tử'
                      aria-label='Reset danh sách hoá đơn điện tử'
                    >
                      <ArrowClockwise
                        key={refreshSpinKey}
                        size={18}
                        className={isResettingList || isFetching ? 'animate-spin' : ''}
                      />
                    </ButtonCommon>
                    <ColumnSelector
                      allColumns={eInvoiceColumnsCheck}
                      selectedColumns={checkedList}
                      onChange={(selected) => setCheckedList(selected)}
                    />
                    {canCreateInvoice && (
                      <ButtonCommon
                        icon={<Plus size={20} />}
                        color='primary'
                        onClick={handleCreateInvoice}
                        size='xs'
                        radius='rounded'
                      >
                        Thêm hoá đơn mới
                      </ButtonCommon>
                    )}
                  </div>
                </div>
              </div>

              <div className='min-h-0 flex-1 px-5 pb-5'>
                {isLoading || isFetching ? (
                  <div className='h-full min-h-0 overflow-hidden rounded-2xl border border-gray-200 bg-white'>
                    <SkeletonTable columns={eInvoiceColumns.length} rows={10} hasHeader={true} />
                  </div>
                ) : (
                  <div className='h-full min-h-0 overflow-hidden rounded-2xl border border-gray-200 bg-white'>
                    <TableCommon<InvoiceRecord>
                      columns={getFilteredColumns()}
                      dataSource={eInvoice || []}
                      defaultPageSize={Number(filteredQuery.size) || 10}
                      count={eInvoiceData?.data?.total || 0}
                      displayView={true}
                      onView={handleView}
                      onEdit={() => {}}
                      onDownload={handleDownloadFile}
                      onAdjust={handleAdjust}
                      onReplace={canCreateInvoice ? handleReplace : undefined}
                      isShowAdjust={(record) => bypassInvoiceEligibilityValidation || canAdjustInvoice(record)}
                      isShowReplace={(record) => bypassInvoiceEligibilityValidation || canReplaceInvoice(record)}
                      tableProps={{
                        scroll: { x: 'max-content' },
                        size: 'middle',
                        className: 'invoice-list-table h-full',
                        loading: false,
                      }}
                      onPaginationChange={handlePagination}
                      currentPage={Number(filteredQuery.page)}
                      searchColumnDefault={parseSimpleFilter(tableState?.filter)}
                      downLoadLoading={isLoadingDownload}
                      idFileDownload={idFileDownload}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceListPage;
